import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import TopicCard from '@/components/base/TopicCard';
import { useSeo } from '@/hooks/useSeo';
import { categories, hotTopics, topics } from '@/mocks/gossip';

type SortKey = 'heat' | 'new' | 'comment';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'heat', label: '按热度' },
  { key: 'new', label: '按最新' },
  { key: 'comment', label: '按讨论量' },
];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState(searchParams.get('category') || '全部');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [sort, setSort] = useState<SortKey>('heat');

  useEffect(() => {
    const cat = searchParams.get('category');
    const q = searchParams.get('q');
    if (cat !== null) setActiveCat(cat);
    if (q !== null) setQuery(q);
  }, [searchParams]);

  const list = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const filtered = topics.filter((topic) => {
      const matchCat = activeCat === '全部' || topic.category === activeCat;
      const matchQuery =
        !keyword ||
        topic.title.toLowerCase().includes(keyword) ||
        topic.excerpt.toLowerCase().includes(keyword) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCat && matchQuery;
    });
    const sorted = filtered.slice();
    if (sort === 'heat') sorted.sort((a, b) => b.heat - a.heat);
    if (sort === 'new') sorted.sort((a, b) => (a.date < b.date ? 1 : -1));
    if (sort === 'comment') sorted.sort((a, b) => b.commentCount - a.commentCount);
    return sorted;
  }, [activeCat, query, sort]);

  const handleCat = (name: string) => {
    setActiveCat(name);
    const next = new URLSearchParams(searchParams);
    if (name === '全部') next.delete('category');
    else next.set('category', name);
    setSearchParams(next, { replace: true });
  };

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '吃瓜热榜 - 瓜田福利社',
        description: '按主题筛选并搜索每日娱乐热点话题，覆盖娱乐现场、影视综艺、音乐现场、粉丝应援等栏目。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '吃瓜热榜', item: '/topics' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: topics.map((topic, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: topic.title,
          url: `/topic/${topic.id}`,
        })),
      },
    ],
    [],
  );

  useSeo({
    title: '吃瓜热榜 - 福利吃瓜话题列表｜话题筛选与搜索 - 瓜田福利社',
    description:
      '按娱乐现场、影视综艺、音乐现场、粉丝应援等栏目筛选福利吃瓜话题，支持关键词搜索与热度排序，快速找到你想参与的讨论与互动活动。',
    keywords: '福利吃瓜话题,吃瓜热榜,娱乐热点话题,福利吃瓜评论区,粉丝投票话题',
    path: '/topics',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-[68px]">
        <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 bg-background-100 border-b border-background-200">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '吃瓜热榜' }]} />
          <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                吃瓜热榜
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                共 {topics.length} 条话题，按热度持续更新。选一个栏目，或者直接搜索你关心的关键词。
              </p>
            </div>
            <div className="w-full lg:w-80">
              <label htmlFor="topic-search" className="block text-xs text-foreground-600 mb-2">
                搜索话题关键词
              </label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  id="topic-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="例如：音乐节、应援、直播"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {['全部', ...categories.map((c) => c.name)].map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleCat(name)}
                    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      activeCat === name
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-background-200">
                <p className="text-sm text-foreground-600">
                  找到 <span className="text-foreground-900">{list.length}</span> 条相关话题
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSort(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        sort === opt.key ? 'bg-background-50 text-primary-600' : 'text-foreground-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {list.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {list.map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              ) : (
                <div className="mt-10 py-16 text-center bg-background-100 rounded-lg border border-background-200">
                  <i className="ri-emotion-unhappy-line text-4xl text-foreground-400"></i>
                  <p className="mt-3 text-sm text-foreground-600">
                    没有找到匹配的话题，换个关键词试试。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('');
                      handleCat('全部');
                    }}
                    className="mt-4 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    重置筛选条件
                  </button>
                </div>
              )}
            </div>

            <aside className="w-full lg:w-72 shrink-0">
              <div className="sticky top-24 space-y-6">
                <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                  <h2 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500"></i>
                    热度榜 TOP6
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {hotTopics.slice(0, 6).map((topic) => (
                      <li key={topic.id} className="flex items-start gap-3">
                        <span
                          className={`shrink-0 w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                            topic.hot <= 3 ? 'bg-accent-500 text-foreground-950' : 'bg-background-300 text-foreground-700'
                          }`}
                        >
                          {topic.hot}
                        </span>
                        <Link
                          to={`/topic/${topic.id}`}
                          className="text-sm text-foreground-800 leading-snug hover:text-primary-600 transition-colors"
                        >
                          {topic.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5">
                  <h2 className="font-heading text-lg text-secondary-900">准备参与互动？</h2>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    打卡、投票与转发抽奖都可以直接开始，规则说明写得很清楚。
                  </p>
                  <Link
                    to="/activities"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-secondary-900 hover:text-primary-600 whitespace-nowrap"
                  >
                    前往互动玩法
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}